/**
 * Runtime API mode resolution.
 *
 * The web UI can run against either the in-memory mock data ("mock" mode) or the
 * real API Gateway ("real" mode). The active mode is resolved ONCE at module
 * load, by the following precedence (highest first):
 *
 *   1. URL query param  ?mock=1|0  (or ?apiMode=mock|real) — persisted to
 *      localStorage so it survives SPA navigation and reloads. ?mock=reset
 *      clears any persisted override.
 *   2. localStorage override        (written by the param above or the dev toggle)
 *   3. Runtime config window.__APP_CONFIG__.apiMode — injected per-deploy from
 *      /config.js (a Kubernetes ConfigMap → container env → entrypoint). This is
 *      what lets ops flip mock<->real on the cluster WITHOUT rebuilding the image.
 *   4. Build-time env VITE_API_MODE (mock | real)
 *   5. Default                      → 'mock'
 *
 * Because the query param wins, a production build can still be run fully mocked
 * on demand (e.g. for demos) simply by appending ?mock=1 to the URL — regardless
 * of what the deployment's runtime config says.
 */

export type ApiMode = 'mock' | 'real'

export const API_MODE_STORAGE_KEY = 'taxhive-api-mode'

const QUERY_RESET_VALUE = 'reset'

function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

function normalizeMode(value: string | null | undefined): ApiMode | null {
  if (!value) return null
  const v = value.trim().toLowerCase()
  if (v === 'mock' || v === 'real') return v
  // ?mock=1 / ?mock=true → mock ; ?mock=0 / ?mock=false → real
  if (v === '1' || v === 'true' || v === 'yes' || v === 'on') return 'mock'
  if (v === '0' || v === 'false' || v === 'no' || v === 'off') return 'real'
  return null
}

/**
 * Read (and consume) a mode override from the URL query string.
 * Recognised params: `mock` and `apiMode`. Persists the result to localStorage
 * and returns it. `?mock=reset` clears the persisted override and returns null.
 */
function readQueryOverride(): ApiMode | null {
  if (!isBrowser()) return null

  const params = new URLSearchParams(window.location.search)
  const raw = params.get('mock') ?? params.get('apiMode')
  if (raw === null) return null

  // Explicit reset: drop any persisted override and fall through to env/default.
  if (raw.trim().toLowerCase() === QUERY_RESET_VALUE) {
    safeRemove(API_MODE_STORAGE_KEY)
    return null
  }

  const mode = normalizeMode(raw)
  if (mode) {
    safeWrite(API_MODE_STORAGE_KEY, mode)
  }
  return mode
}

function readStoredOverride(): ApiMode | null {
  return normalizeMode(safeRead(API_MODE_STORAGE_KEY))
}

/** Per-deploy runtime config injected via /config.js (Kubernetes ConfigMap). */
function readRuntimeConfig(): ApiMode | null {
  if (!isBrowser()) return null
  return normalizeMode(window.__APP_CONFIG__?.apiMode)
}

function readEnvDefault(): ApiMode | null {
  return normalizeMode(import.meta.env.VITE_API_MODE as string | undefined)
}

// ---------------------------------------------------------------------------
// Resolve once at module load so every consumer sees a stable value.
// ---------------------------------------------------------------------------

export type ApiDomain =
  | 'auth'
  | 'users'
  | 'sessions'
  | 'audit'
  | 'customers'
  | 'documents'
  | 'reminders'
  | 'notes'
  | 'assessments'
  | 'payments'
  | 'insights'
  | 'tenant'
  | 'securityPolicy'

const API_DOMAINS: ApiDomain[] = [
  'auth',
  'users',
  'sessions',
  'audit',
  'customers',
  'documents',
  'reminders',
  'notes',
  'assessments',
  'payments',
  'insights',
  'tenant',
  'securityPolicy',
]
const DOMAIN_STORAGE_PREFIX = 'taxhive-api-mode:'

function readDomainQueryOverride(domain: ApiDomain): ApiMode | null {
  if (!isBrowser()) return null
  const params = new URLSearchParams(window.location.search)
  const raw = params.get(`mock.${domain}`) ?? params.get(`apiMode.${domain}`)
  if (raw === null) return null
  const key = DOMAIN_STORAGE_PREFIX + domain
  if (raw.trim().toLowerCase() === QUERY_RESET_VALUE) {
    safeRemove(key)
    return null
  }
  const mode = normalizeMode(raw)
  if (mode) safeWrite(key, mode)
  return mode
}

function readDomainStored(domain: ApiDomain): ApiMode | null {
  return normalizeMode(safeRead(DOMAIN_STORAGE_PREFIX + domain))
}

function readDomainRuntimeConfig(domain: ApiDomain): ApiMode | null {
  if (!isBrowser()) return null
  return normalizeMode(window.__APP_CONFIG__?.domainModes?.[domain])
}

// The user's explicit GLOBAL choice (the toggle button → localStorage, or the
// ?mock= query param). Resolved once; readQueryOverride() persists ?mock= so it
// survives reloads. This WINS over the deploy's per-domain config, so the toggle
// actually flips the data source even when the deployment pins domains to real.
const userGlobalOverride: ApiMode | null = readQueryOverride() ?? readStoredOverride()

// Deploy-provided default for a domain (used when the user hasn't overridden).
function deployDomainMode(domain: ApiDomain): ApiMode {
  return readDomainRuntimeConfig(domain) ?? readRuntimeConfig() ?? readEnvDefault() ?? 'mock'
}

// Per-domain resolution (highest first):
//   1. per-domain user override (?mock.<domain>= / its localStorage)
//   2. global user override (the toggle / ?mock=)
//   3. deploy per-domain config → deploy global → env → mock
const resolvedDomainModes: Record<ApiDomain, ApiMode> = (() => {
  const out = {} as Record<ApiDomain, ApiMode>
  for (const d of API_DOMAINS) {
    const perDomainUser = readDomainQueryOverride(d) ?? readDomainStored(d)
    out[d] = perDomainUser ?? userGlobalOverride ?? deployDomainMode(d)
  }
  return out
})()

// Effective GLOBAL mode for the toggle / indicator: the user override, else the
// auth domain's deploy mode (the most meaningful "are we live?" signal).
const resolvedMode: ApiMode = userGlobalOverride ?? deployDomainMode('auth')

export function getApiMode(): ApiMode {
  return resolvedMode
}

export function getDomainMode(domain: ApiDomain): ApiMode {
  return resolvedDomainModes[domain]
}

export function isDomainMock(domain: ApiDomain): boolean {
  return resolvedDomainModes[domain] === 'mock'
}

/**
 * Base URL for the API Gateway, resolved at load time:
 *   runtime config (/config.js) → build env VITE_API_GATEWAY_URL → '/api/v1'.
 * The relative default lets the Vite dev proxy / production nginx handle routing
 * (and avoids CORS); a ConfigMap can override it per-deploy.
 */
export function getApiGatewayUrl(): string {
  const fromRuntime = isBrowser() ? window.__APP_CONFIG__?.apiGatewayUrl : undefined
  return (fromRuntime && fromRuntime.trim()) || import.meta.env.VITE_API_GATEWAY_URL || '/api/v1'
}

/** Runtime kill switch for additive V2 return-preparation entry points. */
export function isReturnPreparationV2Enabled(): boolean {
  const runtime = isBrowser() ? window.__APP_CONFIG__?.returnPreparationV2Enabled : undefined
  const raw = runtime ?? import.meta.env.VITE_ENABLE_RETURN_PREPARATION_V2 ?? 'true'
  if (typeof raw === 'boolean') return raw
  return !['0', 'false', 'no', 'off'].includes(raw.trim().toLowerCase())
}

/** Default-off cutover for Customer-authorized legacy assessment history. */
export function isClientOwnedAssessmentPagesEnabled(): boolean {
  const runtime = isBrowser() ? window.__APP_CONFIG__?.clientOwnedAssessmentPagesEnabled : undefined
  const raw = runtime ?? import.meta.env.VITE_ENABLE_CLIENT_OWNED_ASSESSMENT_PAGES ?? 'false'
  if (typeof raw === 'boolean') return raw
  return !['0', 'false', 'no', 'off'].includes(raw.trim().toLowerCase())
}

export function isMockMode(): boolean {
  return resolvedMode === 'mock'
}

export function isRealMode(): boolean {
  return resolvedMode === 'real'
}

/**
 * True when the active mode came from an explicit override (query param or
 * localStorage) rather than the build default. Used to decide whether to show
 * the mode indicator in production.
 */
export function hasModeOverride(): boolean {
  return readStoredOverride() !== null
}

/**
 * Persist a new mode and reload so every service factory and store re-resolves
 * against it. Returns nothing; the page navigation ends the current execution.
 */
export function setApiMode(mode: ApiMode): void {
  safeWrite(API_MODE_STORAGE_KEY, mode)
  if (isBrowser()) {
    // Strip any one-shot ?mock= param so it doesn't fight the stored value.
    const url = new URL(window.location.href)
    url.searchParams.delete('mock')
    url.searchParams.delete('apiMode')
    window.location.replace(url.toString())
  }
}

/** Clear the persisted override and reload (revert to env/default). */
export function clearApiMode(): void {
  safeRemove(API_MODE_STORAGE_KEY)
  if (isBrowser()) window.location.reload()
}

// ---------------------------------------------------------------------------
// localStorage helpers — tolerant of Safari private mode / SSR prerender.
// ---------------------------------------------------------------------------

function safeRead(key: string): string | null {
  try {
    return isBrowser() ? window.localStorage.getItem(key) : null
  } catch {
    return null
  }
}

function safeWrite(key: string, value: string): void {
  try {
    if (isBrowser()) window.localStorage.setItem(key, value)
  } catch {
    /* ignore */
  }
}

function safeRemove(key: string): void {
  try {
    if (isBrowser()) window.localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}
